import { useFormikContext } from 'formik';

import {
  SourcesProvider,
  SourcesSourceType,
} from '@/react/portainer/generated-api/portainer/types.gen';

import { BoxSelector, BoxSelectorOption } from '@@/BoxSelector';
import { AddButton } from '@@/buttons';
import { TextTip } from '@@/Tip/TextTip';

import { useSources } from '../../../sources/queries/useSources';
import { validationSchema } from '../validation';
import { WizardStep } from '../WizardContext';
import { FormValues } from '../type';
import { SourceId } from '../../../sources/types';
import { GitIcon } from '../../../sources/components/git/GitIcon';

import { StepTitle } from './StepTitle';

export const SourceSelectStep: WizardStep = {
  id: 'source',
  label: 'Select sources',
  Component,
  validateStep,
};

function getIcon(type: SourcesSourceType, provider?: SourcesProvider) {
  switch (type) {
    case 'git':
      return <GitIcon provider={provider} />;
    case 'helm':
    case 'oci':
    default:
      return null;
  }
}

function Component() {
  const { data, isLoading } = useSources({ type: 'git' });
  const { values, setValues } = useFormikContext<FormValues>();

  if (isLoading) {
    return null;
  }

  const options: BoxSelectorOption<SourceId>[] =
    data?.data?.map(({ id, name, url, type, provider }) => ({
      id: `${id}`,
      label: name,
      value: id,
      icon: getIcon(type, provider),
      description: (
        <div className="overflow-hidden text-ellipsis whitespace-nowrap">
          <span className="text-muted">{url}</span>
        </div>
      ),
    })) || [];

  return (
    <>
      <StepTitle
        title="Select source(s)"
        subtitle="Choose a GitOps source to pull a deployment artifact from."
      />

      {options.length === 0 ? (
        <div className="flex items-center justify-between gap-4">
          <TextTip color="blue">
            No Git sources available. Create one to continue.
          </TextTip>
          <AddButton
            to="portainer.gitops.sources.new"
            data-cy="create-source-button"
          >
            Create new source
          </AddButton>
        </div>
      ) : (
        <BoxSelector
          onChange={(source) =>
            setValues((values) => ({ ...values, sources: [source] }))
          }
          options={options}
          radioName="source"
          value={values.sources[0]}
          useGridLayout
        />
      )}
    </>
  );
}

function validateStep() {
  return validationSchema().pick(['sources']);
}
